import axios from 'axios'
import appConfig from 'configs/app.config'
import { TOKEN_TYPE, REQUEST_HEADER_AUTH_KEY } from 'constants/api.constant'
import store from '../store'
import { onSignInSuccess, onSignOutSuccess } from '../store/auth/sessionSlice'
import { getAccessToken, clearAuthTokens } from 'utils/authTokens'
import { refreshAccessToken } from './TokenRefreshService'
import { trimRequestBody } from 'utils/whitespaceValidation'

const unauthorizedCode = [401]

/**
 * Endpoints that must never trigger a token refresh:
 * - the refresh endpoint itself (recursion)
 * - login/logout (a 401 there means bad credentials, not an expired session)
 */
const NO_REFRESH_URLS = [
    'Account/refresh-token',
    'Account/login',
    'Account/logout',
]

const shouldAttemptRefresh = (config) => {
    const url = config?.url || ''
    return !NO_REFRESH_URLS.some((skip) => url.includes(skip))
}

// Note: the instance-level Authorization header was previously set from
// localStorage("access_token") at module-load time and never refreshed, so
// after login the header held the *empty* pre-login value on the axios
// instance for the lifetime of the page. The request interceptor below
// overrides `Authorization` per-request from the current token, which is the
// actual source of truth.
const BaseService = axios.create({
    timeout: 60000,
    mode: 'no-cors',
    baseURL: appConfig.apiPrefix,
    headers: {
        "Access-Control-Allow-Headers": "origin, content-type, accept",
        'Access-Control-Allow-Origin': '*',
        'Access-Control-Allow-Methods': 'GET,POST,PATCH,PUT,DELETE,OPTIONS',
    }
})

BaseService.interceptors.request.use(config => {
    // A request being REPLAYED after a token refresh already carries the freshly
    // minted token, put there by the 401 handler below. Leave it alone.
    //
    // Without this guard the replay silently reverted to the expired token and the
    // user was signed out roughly every 15 minutes (one access-token lifetime):
    // getAccessToken() prefers the redux-persist blob, and redux-persist flushes to
    // localStorage on setInterval(0) -- a MACROTASK. Axios runs its interceptors in
    // the promise chain (microtasks), so at replay time the blob still held the OLD
    // token and overwrote the new one. The replay then 401'd, `_retriedAfterRefresh`
    // blocked a second attempt, and forceSignOut() ran even though the refresh had
    // succeeded server-side.
    if (config._retriedAfterRefresh && config.headers?.[REQUEST_HEADER_AUTH_KEY]) {
        return config
    }

    const accessToken = getAccessToken()

    if (accessToken) {
        config.headers[REQUEST_HEADER_AUTH_KEY] = `${TOKEN_TYPE}${accessToken}`
    }

    // Strip leading/trailing whitespace from every string in the payload, at the
    // one point every screen's save funnels through. The yup guard
    // (utils/whitespaceValidation) stops a whitespace-ONLY value from being
    // submitted; this stops padding -- "Delhi HQ " -- from being stored, which
    // is what makes a name look duplicated in a list and breaks equality checks
    // server-side. Doing it here rather than in the ~84 form screens means a new
    // module gets it for free. Binary bodies (file uploads) and password fields
    // are passed through untouched.
    config.data = trimRequestBody(config.data)
    config.params = trimRequestBody(config.params)

    return config
}, error => {
    return Promise.reject(error)
})

/**
 * Signs the user out locally. Called only when the session genuinely cannot be
 * renewed — never merely because an access token expired.
 */
const forceSignOut = () => {
    clearAuthTokens()
    store.dispatch(onSignOutSuccess())
}

BaseService.interceptors.response.use(
    response => response,
    async error => {

        const { response, config } = error

        // BUG-004: a save rejected because someone else changed the row arrives as
        // HTTP 409 with a ProblemDetails body. Axios's own message for that is
        // "Request failed with status code 409", which every screen's catch block
        // shows verbatim. Lift the server's explanation onto the error and flag the
        // conflict, so a form can say "reload and re-apply" without each one
        // knowing the wire format.
        if (response && response.status === 409) {
            error.isConcurrencyConflict = true
            const body = response.data || {}
            error.message =
                body.message || body.detail || body.Message ||
                'This record was changed by someone else. Reload and apply your changes again.'
        }

        if (!response || !unauthorizedCode.includes(response.status)) {
            return Promise.reject(error)
        }

        // --- 401 handling -------------------------------------------------
        // An expired ACCESS token is routine and must not cost the user their
        // work: renew it once and replay the request. Only when renewal itself
        // fails is this a real sign-out.

        // `_retriedAfterRefresh` is the loop guard: a request is replayed at most
        // once. If the replay also 401s, the new token is not being accepted and
        // retrying again would spin forever.
        if (!config || config._retriedAfterRefresh || !shouldAttemptRefresh(config)) {
            forceSignOut()
            return Promise.reject(error)
        }

        config._retriedAfterRefresh = true

        try {
            // Single-flight: concurrent 401s share one refresh round-trip.
            const newAccessToken = await refreshAccessToken()

            // Keep redux in step so components reading state.auth.session.token
            // (and the request interceptor's persisted lookup) see the new value.
            store.dispatch(onSignInSuccess(newAccessToken))

            config.headers = config.headers || {}
            config.headers[REQUEST_HEADER_AUTH_KEY] = `${TOKEN_TYPE}${newAccessToken}`

            return BaseService(config)
        } catch (refreshError) {
            // Refresh token missing, expired, revoked, or flagged as reused.
            forceSignOut()
            return Promise.reject(error)
        }
    }
)

export default BaseService
